import { useState, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  CircularProgress,
} from '@mui/material';
import type { CreateStockRequest } from '../../../types/stock';
import { MIN_SEARCH_LENGTH } from '../../../utils/searchConstants';

interface CreateStockProps {
  open: boolean;
  companyId: number;
  onClose: () => void;
  onSubmit: (stockData: CreateStockRequest) => Promise<void>;
}

export default function CreateStock({
  open,
  companyId,
  onClose,
  onSubmit,
}: CreateStockProps) {
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleTransitionEnd = () => {
    if (open && inputRef.current) {
      inputRef.current.focus();
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!name.trim()) {
      setError("Назва складу обов'язкова");
      return;
    }

    if (name.trim().length < MIN_SEARCH_LENGTH) {
      setError(`Назва складу має містити щонайменше ${MIN_SEARCH_LENGTH} символів`);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await onSubmit({
        companyId,
        name: name.trim(),
      });
      
      // Reset form and close dialog on success
      setName('');
      onClose();
    } catch (err) {
      console.error('Error creating stock:', err);
      setError('Не вдалося створити склад. Спробуйте ще раз.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setName('');
      setError(null);
      onClose();
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      slotProps={{
        transition: {
          onEntered: handleTransitionEnd,
        }
      }}
    >
      <form onSubmit={handleSubmit}>
        <DialogTitle>Створити новий склад</DialogTitle>
        <DialogContent>
          <TextField
            inputRef={inputRef}
            fullWidth
            label="Назва складу"
            value={name}
            onChange={(e) => setName(e.target.value)}
            error={!!error}
            helperText={error}
            disabled={loading}
            required
            margin="normal"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={loading}>
            Скасувати
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={loading || !name.trim()}
          >
            {loading ? <CircularProgress size={24} /> : 'Створити'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
